// src/components/VoucherGameClient.tsx
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Gift,
  ShieldCheck,
  Sparkles,
  Star,
  Ticket,
  CheckCircle2,
} from "lucide-react";

import Field from "../components/ui/Field";

import {
  redeemPrintedVoucher,
  validatePrintedVoucher,
  PrizeDto,
} from "../api/printedVoucherGameApi";

import WheelGame from "../pages/VoucherGame/WheelGame";
import GiftBoxGame from "../pages/VoucherGame/GiftBoxGame";

type Step = "ENTER" | "PICK" | "PLAY" | "RESULT";
type Mode = "SPIN" | "GIFTBOX";

const REWARD_STORAGE_KEY = "printedVoucherReward";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function getApiErrorMessage(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  const backendMsg =
    (typeof data?.message === "string" && data.message) ||
    (typeof data?.error === "string" && data.error) ||
    (typeof data === "string" && data) ||
    "";

  if (status === 401) return "Bạn cần đăng nhập để chơi voucher game.";
  if (status === 409) return "Mã vừa bị người khác sử dụng.";
  if (status === 429) return backendMsg || "Hệ thống đang bận. Vui lòng thử lại.";
  if (status === 400 && data?.reason === "LOCK_TIMEOUT") {
    return backendMsg || "Hệ thống đang bận. Vui lòng thử lại.";
  }

  return backendMsg || e?.message || "Có lỗi xảy ra";
}

function StepPill(props: {
  active?: boolean;
  done?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "inline-flex h-10 items-center rounded-full border px-4 text-[11px] font-medium tracking-[0.08em] transition",
        props.active || props.done
          ? "border-brand-brown bg-brand-brown text-brand-ivory"
          : "border-black/10 bg-white/55 text-black/55",
      )}
    >
      {props.children}
    </div>
  );
}

function PrimaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex h-12 w-full items-center justify-center gap-3 rounded-full bg-brand-brown px-6",
        "text-[11px] font-medium tracking-[0.08em] text-brand-ivory transition hover:opacity-95",
        "disabled:cursor-not-allowed disabled:opacity-40",
        props.className,
      )}
    />
  );
}

function SecondaryButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement>,
) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex h-12 w-full items-center justify-center gap-3 rounded-full border border-black/14 bg-white/62 px-6",
        "text-[11px] font-medium tracking-[0.08em] text-black/70 transition hover:bg-white/82",
        "disabled:cursor-not-allowed disabled:opacity-40",
        props.className,
      )}
    />
  );
}

function InfoCard(props: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <div className="rounded-[22px] border border-black/10 bg-white/55 p-4 backdrop-blur-sm">
      <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-black/10 bg-black/[0.03]">
        {props.icon}
      </div>
      <p className="mt-4 text-[11px] font-medium tracking-[0.08em] text-black/75">
        {props.title}
      </p>
      <p className="mt-2 text-sm leading-7 text-black/52">{props.desc}</p>
    </div>
  );
}

function RewardStat(props: { label: string; value: string }) {
  return (
    <div className="rounded-[18px] border border-black/10 bg-white/55 px-4 py-3">
      <p className="text-[11px] font-medium tracking-[0.08em] text-black/44">
        {props.label}
      </p>
      <p className="mt-2 text-sm font-medium text-black/78">{props.value}</p>
    </div>
  );
}

export default function VoucherGameClient() {
  const [code, setCode] = useState<string>("");
  const [step, setStep] = useState<Step>("ENTER");
  const [mode, setMode] = useState<Mode | null>(null);

  const [allowed, setAllowed] = useState<string[]>([]);
  const [prize, setPrize] = useState<PrizeDto | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const redeemingRef = useRef(false);

  const canSpin = useMemo(() => allowed.includes("SPIN"), [allowed]);
  const canGiftbox = useMemo(() => allowed.includes("GIFTBOX"), [allowed]);

  const resetAll = () => {
    if (loading || redeemingRef.current) return;
    setCode("");
    setAllowed([]);
    setPrize(null);
    setMode(null);
    setError("");
    setStep("ENTER");
  };

  const resetToPick = () => {
    if (loading || redeemingRef.current) return;
    setPrize(null);
    setMode(null);
    setError("");
    setStep("PICK");
  };

  const handleValidate = async () => {
    const c = code.trim();
    if (!c) {
      setError("Vui lòng nhập mã voucher.");
      return;
    }

    if (loading) return;

    setLoading(true);
    setError("");

    try {
      const res = await validatePrintedVoucher(c);

      if (!res.ok) {
        setError(res.message || "Mã không hợp lệ.");
        return;
      }

      if (res.alreadyRedeemed) {
        setError(
          `Mã này đã được sử dụng${
            res.redeemedAt
              ? ` lúc ${new Date(res.redeemedAt).toLocaleString("vi-VN")}`
              : ""
          }.`,
        );
        return;
      }

      const allow = res.allowedExperiences || [];
      setAllowed(allow);

      if (!allow.length) {
        setError("Mã hợp lệ nhưng không có trải nghiệm nào được phép.");
        return;
      }

      if (
        allow.length === 1 &&
        (allow[0] === "SPIN" || allow[0] === "GIFTBOX")
      ) {
        setMode(allow[0] as Mode);
        setStep("PLAY");
      } else {
        setStep("PICK");
      }
    } catch (e: any) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const redeem = async (experience: Mode): Promise<PrizeDto | null> => {
    const c = code.trim();
    if (!c) {
      setError("Thiếu mã voucher.");
      return null;
    }

    if (redeemingRef.current) {
      setError("Đang xử lý, vui lòng đợi...");
      return null;
    }

    redeemingRef.current = true;
    setLoading(true);
    setError("");

    try {
      const res = await redeemPrintedVoucher(c, experience);

      if (!res.ok) {
        setError(res.message || "Đổi thưởng thất bại.");
        return null;
      }

      if (!res.prize) {
        setError("Không nhận được phần thưởng.");
        return null;
      }

      setPrize(res.prize);
      localStorage.setItem(REWARD_STORAGE_KEY, JSON.stringify(res.prize));
      setStep("RESULT");

      return res.prize;
    } catch (e: any) {
      setError(getApiErrorMessage(e));
      return null;
    } finally {
      redeemingRef.current = false;
      setLoading(false);
    }
  };

  const stepTitle =
    step === "ENTER"
      ? "Enter code"
      : step === "PICK"
        ? "Pick mode"
        : step === "PLAY"
          ? "Play"
          : "Result";

  return (
    <div className="min-h-[calc(100vh-120px)] bg-brand-ivory px-6 py-14 text-brand-brown sm:py-16">
      <div className="mx-auto w-full max-w-6xl">
        {/* HERO */}
        <section className="pb-10 sm:pb-12">
          <div className="grid items-end gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <div className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white/58 px-4 py-2 backdrop-blur-sm">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.03]">
                  <Sparkles className="h-4 w-4 text-brand-gold" />
                </span>
                <span className="text-[11px] font-medium tracking-[0.1em] text-black/62">
                  Voucher experience
                </span>
              </div>

              <p className="mt-8 text-[11px] font-medium tracking-[0.12em] text-black/44">
                Reward page
              </p>

              <h1 className="mt-4 text-[32px] font-medium leading-[1.02] tracking-[0.01em] text-brand-brown sm:text-[44px] lg:text-[56px]">
                Unlock a private reward experience.
              </h1>

              <p className="mt-5 max-w-xl text-sm leading-8 text-black/58 sm:text-[15px]">
                Nhập mã voucher để mở trải nghiệm nhận quà. Tùy mã hợp lệ, bạn
                sẽ được tham gia vòng quay hoặc mở hộp quà để nhận reward dành
                riêng cho đơn hàng của mình.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <StepPill active={step === "ENTER"} done={step !== "ENTER"}>
                  Enter code
                </StepPill>
                <StepPill
                  active={step === "PICK"}
                  done={step === "PLAY" || step === "RESULT"}
                >
                  Pick mode
                </StepPill>
                <StepPill active={step === "PLAY"} done={step === "RESULT"}>
                  Play
                </StepPill>
                <StepPill active={step === "RESULT"}>Result</StepPill>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <InfoCard
                icon={<Ticket className="h-4 w-4 text-brand-gold" />}
                title="Voucher code"
                desc="Mỗi mã hợp lệ sẽ mở đúng trải nghiệm được cho phép."
              />
              <InfoCard
                icon={<Gift className="h-4 w-4 text-brand-gold" />}
                title="Random reward"
                desc="Phần quà được trả về ngay sau khi bạn chơi thành công."
              />
              <InfoCard
                icon={<ShieldCheck className="h-4 w-4 text-brand-gold" />}
                title="Secure redeem"
                desc="Một mã chỉ sử dụng được đúng quyền chơi của nó."
              />
            </div>
          </div>
        </section>

        {/* STATUS BAR */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-[22px] border border-black/10 bg-white/52 px-4 py-4 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-black/10 bg-black/[0.03]">
              <Star className="h-4 w-4 text-brand-gold" />
            </div>
            <div>
              <p className="text-[11px] font-medium tracking-[0.08em] text-black/45">
                Current step
              </p>
              <p className="mt-1 text-sm font-medium text-brand-brown">
                {stepTitle}
              </p>
            </div>
          </div>

          <p className="text-[11px] font-medium tracking-[0.08em] text-black/42">
            Secure play · Private reward
          </p>
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-6 rounded-[22px] border border-red-500/15 bg-red-500/5 px-4 py-4">
            <p className="text-sm leading-7 text-red-700">
              <span className="font-semibold">Lỗi:</span> {error}
            </p>
          </div>
        )}

        {/* MAIN CARD */}
        <div
          className="rounded-[34px] border border-black/10 bg-white/55 px-5 py-6 shadow-[0_30px_90px_rgba(43,31,26,0.06)]
                     backdrop-blur-sm sm:px-7 sm:py-7 lg:px-10 lg:py-10"
        >
          {/* STEP ENTER */}
          {step === "ENTER" && (
            <div className="grid gap-10 lg:grid-cols-[0.88fr_1.12fr] lg:items-center">
              <div>
                <p className="text-[11px] font-medium tracking-[0.12em] text-black/44">
                  Enter your code
                </p>
                <h2 className="mt-4 text-[28px] font-medium leading-[1.02] tracking-[0.01em] text-brand-brown sm:text-[36px]">
                  Start your voucher game.
                </h2>
                <p className="mt-5 text-sm leading-8 text-black/58">
                  Nhập mã voucher in sẵn để hệ thống kiểm tra quyền chơi. Khi mã
                  hợp lệ, bạn sẽ được chuyển sang bước chọn trải nghiệm hoặc vào
                  chơi ngay nếu chỉ có một chế độ.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.08em] text-black/62 transition hover:text-black"
                  >
                    Về trang chủ
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              <div className="rounded-[28px] border border-black/10 bg-white/62 p-5 sm:p-6">
                <div className="space-y-6">
                  <Field label="Mã voucher" error={error || undefined}>
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleValidate();
                      }}
                      disabled={loading}
                      placeholder="VD: OWL-XXXX-XXXX"
                      className="lux-input"
                    />
                  </Field>

                  <PrimaryButton
                    type="button"
                    onClick={handleValidate}
                    disabled={loading}
                  >
                    {loading ? "Đang kiểm tra..." : "Tiếp tục"}
                    {!loading && <ArrowUpRight className="h-4 w-4" />}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          )}

          {/* STEP PICK */}
          {step === "PICK" && (
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
              <div>
                <p className="text-[11px] font-medium tracking-[0.12em] text-black/44">
                  Pick your mode
                </p>
                <h2 className="mt-4 text-[28px] font-medium leading-[1.02] tracking-[0.01em] text-brand-brown sm:text-[36px]">
                  Choose how you want to play.
                </h2>
                <p className="mt-5 text-sm leading-8 text-black/58">
                  Mã voucher của bạn cho phép một hoặc nhiều trải nghiệm. Chọn
                  chế độ bạn muốn để bắt đầu mở thưởng.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {canSpin && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      if (loading) return;
                      setMode("SPIN");
                      setStep("PLAY");
                    }}
                    className="group rounded-[28px] border border-black/10 bg-brand-brown p-6 text-left text-brand-ivory transition hover:opacity-95"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/12 bg-white/10">
                      <Sparkles className="h-5 w-5 text-brand-gold" />
                    </div>
                    <p className="mt-5 text-[11px] font-medium tracking-[0.12em] text-white/72">
                      Mode
                    </p>
                    <h3 className="mt-2 text-[22px] font-medium tracking-[0.01em]">
                      Vòng quay
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-white/72">
                      Spin để nhận phần thưởng ngẫu nhiên từ hệ thống.
                    </p>
                  </button>
                )}

                {canGiftbox && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      if (loading) return;
                      setMode("GIFTBOX");
                      setStep("PLAY");
                    }}
                    className="group rounded-[28px] border border-black/10 bg-white/62 p-6 text-left transition hover:bg-white/78"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-black/[0.03]">
                      <Gift className="h-5 w-5 text-brand-gold" />
                    </div>
                    <p className="mt-5 text-[11px] font-medium tracking-[0.12em] text-black/46">
                      Mode
                    </p>
                    <h3 className="mt-2 text-[22px] font-medium tracking-[0.01em] text-brand-brown">
                      Hộp quà
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-black/58">
                      Chọn một hộp quà để mở phần thưởng của bạn.
                    </p>
                  </button>
                )}

                <div className="sm:col-span-2">
                  <SecondaryButton
                    type="button"
                    onClick={resetAll}
                    disabled={loading}
                  >
                    Nhập mã khác
                  </SecondaryButton>
                </div>
              </div>
            </div>
          )}

          {/* STEP PLAY */}
          {step === "PLAY" && mode && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-medium tracking-[0.12em] text-black/44">
                    Now playing
                  </p>
                  <h2 className="mt-3 text-[24px] font-medium leading-[1.02] tracking-[0.01em] text-brand-brown sm:text-[32px]">
                    {mode === "SPIN" ? "Vòng quay" : "Hộp quà"}
                  </h2>
                </div>

                <SecondaryButton
                  type="button"
                  onClick={resetToPick}
                  disabled={loading}
                  className="w-auto px-6"
                >
                  Đổi chế độ
                </SecondaryButton>
              </div>

              <div className="rounded-[28px] border border-black/10 bg-white/50 px-3 py-4 sm:px-4 sm:py-5">
                {mode === "SPIN" ? (
                  <WheelGame
                    onSpin={async () => {
                      const p = await redeem("SPIN");
                      if (!p) return Promise.reject(new Error("REDEEM_FAILED"));
                      return p;
                    }}
                    onDone={(p) => setPrize(p)}
                  />
                ) : (
                  <GiftBoxGame
                    count={9}
                    onPick={async () => {
                      const p = await redeem("GIFTBOX");
                      if (!p) return Promise.reject(new Error("REDEEM_FAILED"));
                      return p;
                    }}
                    onDone={(p) => setPrize(p)}
                  />
                )}
              </div>

              {loading && (
                <div className="text-center">
                  <p className="text-[11px] font-medium tracking-[0.08em] text-black/44">
                    Đang xử lý...
                  </p>
                </div>
              )}

              <SecondaryButton
                type="button"
                onClick={resetAll}
                disabled={loading}
              >
                Nhập mã khác
              </SecondaryButton>
            </div>
          )}

          {/* STEP RESULT */}
          {step === "RESULT" && prize && (
            <div className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-green-500/15 bg-green-500/5 px-4 py-2 text-[11px] font-medium tracking-[0.08em] text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  Reward unlocked
                </div>

                <h2 className="mt-5 text-[28px] font-medium leading-[1.02] tracking-[0.01em] text-brand-brown sm:text-[36px]">
                  Chúc mừng, bạn đã nhận được phần thưởng.
                </h2>
                <p className="mt-5 text-sm leading-8 text-black/58">
                  Reward của bạn đã được ghi nhận. Bạn có thể dùng khi mua hàng
                  hoặc tiếp tục nhập mã khác nếu còn voucher.
                </p>
              </div>

              <div className="rounded-[30px] border border-black/10 bg-white/62 p-5 sm:p-6">
                <div className="rounded-[24px] border border-black/10 bg-brand-ivory/80 p-4 sm:p-5">
                  <p className="text-[11px] font-medium tracking-[0.08em] text-black/48">
                    Reward
                  </p>
                  <p className="mt-3 text-[22px] font-medium leading-8 tracking-[0.01em] text-brand-brown">
                    {prize.name}
                  </p>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <RewardStat label="Type" value={prize.type} />
                    <RewardStat label="Code" value={prize.code} />
                  </div>
                </div>

                <div className="mt-6 grid gap-3">
                  <PrimaryButton
                    type="button"
                    onClick={() => (window.location.href = "/cart")}
                  >
                    Dùng khi mua hàng
                    <ArrowUpRight className="h-4 w-4" />
                  </PrimaryButton>

                  <SecondaryButton
                    type="button"
                    onClick={resetAll}
                    disabled={loading}
                  >
                    Nhập mã khác
                  </SecondaryButton>

                  <Link
                    to="/"
                    className="inline-flex justify-center gap-2 pt-2 text-[11px] font-medium tracking-[0.08em] text-black/58 transition hover:text-black"
                  >
                    Về trang chủ
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="mt-8 text-[11px] font-medium tracking-[0.08em] text-black/34">
          Secure play · Private reward
        </p>
      </div>
    </div>
  );
}